import React, { useState, useContext, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  AlertCircle,
  Globe,
  Coins,
  RefreshCw,
  Package,
  X,
  Loader2,
  Star,
  CheckCircle2,
  DollarSign,
  Mail,
  User,
} from "lucide-react";
import marketplaceService from "../services/marketplaceService";
import walletService from "../services/walletService";
import AuthContext from "../context/AuthContext";
import { countryName, currencyName } from "../utils/catalogLabels";
import { useTranslation, useFormat } from "../i18n";

// Format a monetary amount in the gift card's own currency (e.g., USD, GBP, EUR)
// using the locale-aware formatter from useFormat().
const formatMoney = (fmt, amount, currency = "USD") => {
  const value = Number(amount) || 0;
  return fmt.currency(value, currency, {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
  });
};

// Human-readable summary of where a gift card can be redeemed.
const availabilityLabel = (t, locale, countries = []) => {
  if (!countries.length) return t("card.availability.worldwide");
  const names = countries.map((code) => countryName(code, locale));
  if (names.length === 1) return t("card.availability.one", { first: names[0] });
  if (names.length === 2) return t("card.availability.two", { first: names[0], second: names[1] });
  return t("card.availability.many", { first: names[0], second: names[1], more: names.length - 2 });
};

// Catalog Item Card Component
const CatalogItemCard = ({ item, onRedeem, userPoints = 0, pointsToDollarRate = 1.0 }) => {
  const { t } = useTranslation("marketplace");
  const fmt = useFormat();
  const currency = item.currency || "USD";
  // Convert min/max values to points using conversion rate
  const minPointsCost = item.min_value ? Math.ceil(item.min_value / pointsToDollarRate) : 0;
  const maxPointsCost = item.max_value ? Math.ceil(item.max_value / pointsToDollarRate) : 0;
  const canAfford = userPoints >= minPointsCost;
  const isAvailable = item.status === "active" && (item.stock === null || item.stock > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 transition-all duration-200 hover:shadow-xl hover:-translate-y-1 hover:border-purple-300"
    >
      {/* Image or placeholder */}
      <div className="h-48 org-gradient-light flex items-center justify-center relative overflow-hidden">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-contain p-4"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`w-full h-full flex items-center justify-center ${item.image_url ? 'hidden' : ''}`}>
          <Package className="w-16 h-16 text-purple-300" />
        </div>

        {/* Availability Badge */}
        <span
          className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${
            isAvailable
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {isAvailable ? t("card.available") : t("card.unavailable")}
        </span>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Name */}
        <h3 className="font-semibold text-gray-900 text-lg line-clamp-2 min-h-[3.5rem]">
          {item.name}
        </h3>

        {/* Description */}
        {item.description && (
          <p className="text-sm text-gray-600 line-clamp-2">
            {item.description}
          </p>
        )}

        {/* Stock info */}
        {item.stock !== null && (
          <p className="text-xs text-gray-500">
            {item.stock > 0 ? t("card.in_stock", { count: item.stock }) : t("card.out_of_stock")}
          </p>
        )}

        {/* Price in Points */}
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          <span className="text-2xl font-bold text-purple-600">
            {minPointsCost && maxPointsCost && minPointsCost !== maxPointsCost
              ? t("card.points_range", { min: fmt.number(minPointsCost), max: fmt.number(maxPointsCost) })
              : fmt.number(minPointsCost || 0)}
          </span>
          <span className="text-gray-500">{t("card.points_unit")}</span>
        </div>
        {/* Show gift card face value in its own currency */}
        <p className="text-xs text-gray-400">
          {item.max_value && item.max_value !== item.min_value
            ? t("card.value_range", { min: formatMoney(fmt, item.min_value, currency), max: formatMoney(fmt, item.max_value, currency) })
            : t("card.value", { amount: formatMoney(fmt, item.min_value, currency) })}
        </p>

        {/* Currency + region availability */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[11px] font-medium text-gray-600">
            <Coins className="w-3 h-3" />
            {currency}
          </span>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[11px] font-medium text-gray-600 max-w-full"
            title={(item.countries || []).map((code) => countryName(code, fmt.locale)).join(", ") || t("card.no_country_restrictions")}
          >
            <Globe className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{availabilityLabel(t, fmt.locale, item.countries)}</span>
          </span>
        </div>

        {/* Redeem Button */}
        <button
          onClick={() => onRedeem(item)}
          disabled={!isAvailable || !canAfford}
          className={`w-full py-2.5 rounded-full font-medium transition-all ${
            !isAvailable
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : !canAfford
              ? "bg-gray-100 text-gray-500 cursor-not-allowed"
              : "org-gradient text-white hover:opacity-90"
          }`}
        >
          {!isAvailable
            ? t("card.unavailable")
            : !canAfford
            ? t("card.insufficient_points")
            : t("card.redeem_now")}
        </button>

        {!canAfford && isAvailable && (
          <p className="text-xs text-red-500 text-center">
            {t("card.need_more_points", { count: minPointsCost - userPoints })}
          </p>
        )}
      </div>
    </motion.div>
  );
};

// Redemption Modal Component - Gift Card Style (points to dollar value)
const RedemptionModal = ({
  isOpen,
  onClose,
  item,
  userPoints,
  onConfirm,
  isLoading,
  currentUser,
  pointsToDollarRate = 1.0,
}) => {
  const { t } = useTranslation("marketplace");
  const fmt = useFormat();
  const [amount, setAmount] = useState(item?.min_value || 10);
  const [recipientEmail, setRecipientEmail] = useState(currentUser?.email || "");
  const [recipientFirstName, setRecipientFirstName] = useState(
    currentUser?.full_name?.split(" ")[0] || ""
  );
  const [recipientLastName, setRecipientLastName] = useState(
    currentUser?.full_name?.split(" ").slice(1).join(" ") || ""
  );
  const [sendToSelf, setSendToSelf] = useState(true);

  if (!isOpen || !item) return null;

  const currency = item.currency || "USD";
  const minValue = item.min_value || 1;
  const maxValue = item.max_value || 500;

  // Calculate points cost based on conversion rate
  // rate = dollars per point (e.g., 0.10 means 1 point = $0.10)
  // So points needed = amount / rate
  const pointsCost = Math.ceil(amount / pointsToDollarRate);
  const canProceed = userPoints >= pointsCost && amount >= minValue && amount <= maxValue && recipientEmail && recipientFirstName && recipientLastName;
  const remainingPoints = userPoints - pointsCost;

  const handleConfirm = () => {
    onConfirm({
      sku: item.sku || item.id,
      amount_usd: amount,
      recipient_email: recipientEmail,
      recipient_first_name: recipientFirstName,
      recipient_last_name: recipientLastName,
    });
  };

  const handleSendToSelfChange = (checked) => {
    setSendToSelf(checked);
    if (checked && currentUser) {
      setRecipientEmail(currentUser.email || "");
      const nameParts = (currentUser.full_name || "").split(" ");
      setRecipientFirstName(nameParts[0] || "");
      setRecipientLastName(nameParts.slice(1).join(" ") || "");
    }
  };

  // Quick amount buttons
  const quickAmounts = [10, 25, 50, 100].filter(a => a >= minValue && a <= maxValue);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl z-10"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t("modal.title")}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Item Details */}
              <div className="flex items-center gap-4">
                {item.image_url && (
                  <img src={item.image_url} alt={item.name} className="w-16 h-16 object-contain rounded-2xl bg-gray-100 p-2" />
                )}
                <div>
                  <h4 className="font-semibold text-gray-900">{item.name}</h4>
                  <p className="text-sm text-gray-500">
                    {t("modal.range_available", { min: formatMoney(fmt, minValue, currency), max: formatMoney(fmt, maxValue, currency) })}
                  </p>
                </div>
              </div>

              {/* Amount Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  {t("modal.amount_label", { currency })}
                </label>

                {/* Quick amount buttons */}
                {quickAmounts.length > 0 && (
                  <div className="flex gap-2 mb-3">
                    {quickAmounts.map((amt) => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => setAmount(amt)}
                        className={`px-4 py-2 rounded-full border transition-all ${
                          amount === amt
                            ? "border-purple-500 bg-purple-50 text-purple-700"
                            : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        {formatMoney(fmt, amt, currency)}
                      </button>
                    ))}
                  </div>
                )}

                <input
                  type="number"
                  min={minValue}
                  max={maxValue}
                  value={amount}
                  onChange={(e) => setAmount(Math.max(minValue, Math.min(maxValue, parseInt(e.target.value) || minValue)))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t("modal.min_max", { min: formatMoney(fmt, minValue, currency), max: formatMoney(fmt, maxValue, currency) })}
                </p>
              </div>

              {/* Recipient Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendToSelf"
                    checked={sendToSelf}
                    onChange={(e) => handleSendToSelfChange(e.target.checked)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor="sendToSelf" className="text-sm text-gray-700">
                    {t("modal.send_to_self")}
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <User className="w-4 h-4 inline mr-1" />
                      {t("modal.first_name")}
                    </label>
                    <input
                      type="text"
                      value={recipientFirstName}
                      onChange={(e) => setRecipientFirstName(e.target.value)}
                      disabled={sendToSelf}
                      className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                      placeholder={t("modal.first_name_placeholder")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("modal.last_name")}
                    </label>
                    <input
                      type="text"
                      value={recipientLastName}
                      onChange={(e) => setRecipientLastName(e.target.value)}
                      disabled={sendToSelf}
                      className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                      placeholder={t("modal.last_name_placeholder")}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    {t("modal.email_label")}
                  </label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    disabled={sendToSelf}
                    className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                    placeholder={t("modal.email_placeholder")}
                  />
                </div>
              </div>

              {/* Points Summary */}
              <div className="bg-gray-50 rounded-2xl p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t("modal.gift_card_value")}</span>
                  <span className="text-sm font-medium">{formatMoney(fmt, amount, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t("modal.points_cost")}</span>
                  <span className="text-sm font-bold text-purple-600">{t("common:points", { count: pointsCost })}</span>
                </div>
                <hr className="border-gray-200" />
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t("modal.your_points")}</span>
                  <span className="text-sm font-medium">{fmt.number(userPoints)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">{t("modal.after_redemption")}</span>
                  <span className={`text-sm font-bold ${remainingPoints >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {t("common:points", { count: remainingPoints })}
                  </span>
                </div>
              </div>

              {!canProceed && userPoints < pointsCost && (
                <p className="text-sm text-red-500">
                  {t("modal.insufficient", { count: pointsCost - userPoints })}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                {t("common:actions.cancel")}
              </button>
              <button
                onClick={handleConfirm}
                disabled={!canProceed || isLoading}
                className="flex items-center gap-2 px-4 py-2 org-gradient text-white rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("modal.confirm", { amount: formatMoney(fmt, amount, currency) })}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

// Main Marketplace Page
const MarketplacePage = () => {
  const { t } = useTranslation("marketplace");
  const fmt = useFormat();
  const { user: currentUser } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // "all" | "USD" | ...
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Fetch wallet balance
  const {
    data: wallet,
    isLoading: walletLoading,
    error: walletError,
  } = useQuery({
    queryKey: ["wallet"],
    queryFn: () => walletService.getMyWallet(),
  });

  // Fetch catalog (returns { items, pointsToDollarRate })
  const {
    data: catalogData,
    isLoading: catalogLoading,
    error: catalogError,
    refetch,
  } = useQuery({
    queryKey: ["marketplace-catalog"],
    queryFn: () => marketplaceService.getCatalog(),
  });

  // Redemption mutation - uses rewards platform (Tremendous)
  const redemptionMutation = useMutation({
    mutationFn: (redemptionData) =>
      marketplaceService.redeemFromPlatform(redemptionData),
    onSuccess: (data) => {
      showNotification(t("notifications.redeem_success"));
      setIsModalOpen(false);
      setSelectedItem(null);
      queryClient.invalidateQueries({ queryKey: ["wallet"] });
      queryClient.invalidateQueries({ queryKey: ["marketplace-catalog"] });
    },
    onError: (error) => {
      showNotification(error.message || t("notifications.redeem_failed"), "error");
    },
  });

  const handleRedeem = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleConfirmRedemption = (redemptionData) => {
    redemptionMutation.mutate(redemptionData);
  };

  const userPoints = wallet?.balance ?? 0;
  // Extract items and conversion rate from catalog response
  // Memoized so the currency options below don't recompute on every render.
  const items = useMemo(
    () => catalogData?.items || (Array.isArray(catalogData) ? catalogData : []),
    [catalogData]
  );
  const pointsToDollarRate = catalogData?.pointsToDollarRate || 1.0;

  // Currency options present in the catalog, with how many cards use each.
  const currencyOptions = useMemo(() => {
    const counts = new Map();
    items.forEach((item) => {
      const code = (item.currency || "USD").toUpperCase();
      counts.set(code, (counts.get(code) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [items]);

  // Filter items by search query and currency
  const filteredItems = items.filter((item) => {
    if (currencyFilter !== "all" && (item.currency || "USD").toUpperCase() !== currencyFilter) {
      return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query)
    );
  });

  const hasActiveFilters = !!searchQuery || currencyFilter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setCurrencyFilter("all");
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8 pt-3 sm:pt-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Notification */}
        <AnimatePresence>
          {notification && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`fixed top-20 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-2xl shadow-lg ${
                notification.type === "error"
                  ? "bg-red-500 text-white"
                  : "bg-green-500 text-white"
              }`}
            >
              {notification.type === "error" ? (
                <AlertCircle className="w-5 h-5" />
              ) : (
                <CheckCircle2 className="w-5 h-5" />
              )}
              {notification.message}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Points Balance Card */}
        <div className="org-gradient rounded-2xl shadow-lg p-3 sm:p-6 mb-4 sm:mb-8 text-white">
          {/* Mobile: label + balance on one row, star hidden */}
          <div className="flex items-center gap-2 sm:hidden">
            <Star className="w-4 h-4 text-white/70 flex-shrink-0" />
            <p className="text-white/90 text-sm font-medium">{t("balance.title_mobile")}</p>
            {walletLoading ? (
              <span className="text-sm">{t("common:status.loading")}</span>
            ) : walletError ? (
              <span className="text-lg font-bold">--</span>
            ) : (
              <span className="text-lg font-bold ml-auto">{t("balance.points_short", { points: fmt.number(userPoints) })}</span>
            )}
          </div>

          {/* Desktop: current stacked layout with big star */}
          <div className="hidden sm:flex justify-between items-center">
            <div>
              <p className="text-white/80 text-sm font-medium mb-1">
                {t("balance.title")}
              </p>
              {walletLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>{t("common:status.loading")}</span>
                </div>
              ) : walletError ? (
                <div>
                  <p className="text-2xl font-bold mb-2">--</p>
                  <button
                    onClick={() => queryClient.invalidateQueries({ queryKey: ["wallet"] })}
                    className="px-3 py-1 text-xs bg-white/20 hover:bg-white/30 rounded transition-colors flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    {t("common:actions.retry")}
                  </button>
                </div>
              ) : (
                <p className="text-4xl font-bold">
                  {t("balance.points_short", { points: fmt.number(userPoints) })}
                </p>
              )}
            </div>
            <Star className="w-16 h-16 text-white/20" />
          </div>
        </div>

        {/* Search + filters */}
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-3 sm:gap-4">
          {/* Search */}
          <div>
            <label htmlFor="rewards-search" className="block text-sm font-medium text-gray-700 mb-2">
              {t("search.label")}
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="rewards-search"
                type="text"
                placeholder={t("search.placeholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  aria-label={t("search.clear")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Currency */}
          <div>
            <label htmlFor="rewards-currency" className="block text-sm font-medium text-gray-700 mb-2">
              {t("currency.label")}
            </label>
            <div className="relative">
              <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                id="rewards-currency"
                value={currencyFilter}
                onChange={(e) => setCurrencyFilter(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-full bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none"
              >
                <option value="all">{t("currency.all")}</option>
                {currencyOptions.map(({ code, count }) => {
                  const name = currencyName(code, fmt.locale);
                  return (
                    <option key={code} value={code}>
                      {name === code
                        ? t("currency.option", { code, total: count })
                        : t("currency.option_named", { code, name, total: count })}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {/* Catalog Grid */}
        {catalogLoading ? (
          <div className="flex flex-col justify-center items-center py-20">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mb-4" />
            <p className="text-gray-600">{t("catalog.loading")}</p>
          </div>
        ) : catalogError ? (
          <div className="bg-white border border-red-200 rounded-2xl p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-700 mb-2">
              {t("catalog.load_failed")}
            </h3>
            <p className="text-sm text-red-600 mb-4">
              {catalogError.message || t("catalog.try_later")}
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {t("common:actions.retry")}
            </button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-md">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">
              {hasActiveFilters
                ? t("catalog.empty_filtered")
                : t("catalog.empty")}
            </p>
            {hasActiveFilters ? (
              <button
                onClick={clearFilters}
                className="mt-4 text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                {t("catalog.clear_filters")}
              </button>
            ) : (
              <p className="mt-2 text-sm text-gray-500">
                {t("catalog.contact_admin")}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className="text-sm text-gray-500">
                {t("catalog.showing", { shown: fmt.number(filteredItems.length), total: fmt.number(items.length) })}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium whitespace-nowrap"
                >
                  {t("catalog.clear_filters")}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredItems.map((item) => (
                <CatalogItemCard
                  key={item.id}
                  item={item}
                  onRedeem={handleRedeem}
                  userPoints={userPoints}
                  pointsToDollarRate={pointsToDollarRate}
                />
              ))}
            </div>
          </>
        )}

        {/* Redemption Modal */}
        <RedemptionModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
          userPoints={userPoints}
          onConfirm={handleConfirmRedemption}
          isLoading={redemptionMutation.isPending}
          currentUser={currentUser}
          pointsToDollarRate={pointsToDollarRate}
        />
      </div>
    </div>
  );
};

export default MarketplacePage;
